import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { SearchInput } from './search-input';
import { HttpMethod, HttpParams, RestService } from '../rest.service';
import { firstValueFrom } from 'rxjs';
import { CreateCustomer } from './create-customer';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { Address, Customer, PagedCustomerResponse } from './customer';
import { logError } from '../helpers/sentry.helper';

// Configuration de base pour l'API
const WEB_SERVICES = (environment.castorama as any).webServices ?? {};
const BASE_URL = WEB_SERVICES.baseUrl;
const TENANT = WEB_SERVICES.tenant;
const COLLEAGUE_API_VERSION = WEB_SERVICES.colleagueApiVersion || 'v3';

// Constantes pour les messages d'erreur
const ERR_ACCOUNT_EXISTS = 'Un compte avec cette adresse mail existe déjà, veuillez rechercher ce client.';
const ERR_CREATION_FAILED = 'Une erreur est survenue lors de la création du compte';
const ERR_ADDRESS_CREATION_FAILED = "Une erreur est survenue lors de la création de l'adresse";

@Injectable({
  providedIn: 'root',
})
export class KingfisherService extends RestService {
  private readonly _colleagueBaseApi: string;

  constructor() {
    super();

    const environmentUrl = environment.castorama.webServices?.environmentUrl || '';

    this._colleagueBaseApi = `${BASE_URL}${environmentUrl}/colleague/${COLLEAGUE_API_VERSION}/customers/${TENANT}`;
  }

  /**
   * Enregistre un client par un collègue avec un token d'authentification.
   * @param createCustomer Données du client
   * @param token Token du collègue
   */
  async registerByColleague(createCustomer: CreateCustomer, token: string): Promise<Customer> {
    const address = createCustomer.address;
    delete createCustomer.address;

    try {
      const responseCustomerCreate = await this.makeApiCall({
        method: HttpMethod.POST,
        url: `${this._colleagueBaseApi}/createUninitialized`,
        body: {
          data: {
            type: 'uninitializedCustomerCreate',
            attributes: createCustomer,
          },
        },
        token,
        withCredentials: false,
      });

      const customerId = responseCustomerCreate.data.id;
      //Si le client a saisi une adresse complète, on ajoute l'adresse, c'est une double sécurité normalement le form protège ce cas la
      if (this.validateAddress(address)) {
        createCustomer.address = address;
        await this.createCustomerAddress(customerId, createCustomer, token);
      }

      return await this.getCustomerInfoByColleague(token, customerId);
    } catch (err) {
      logError(err);
      if (err?.status === 409) throw new Error(ERR_ACCOUNT_EXISTS);

      throw new Error(`${ERR_CREATION_FAILED} : ${err?.error?.errors[0].detail}`);
    }
  }

  /**
   * Récupère les informations d'un client pour un collègue
   */
  async getCustomerInfoByColleague(token: string, customerKfId: string): Promise<Customer> {
    const result = await this.makeApiCall({
      method: HttpMethod.GET,
      url: `${this._colleagueBaseApi}/${customerKfId}`,
      token,
      withCredentials: false,
    });
    return plainToInstance(Customer, result?.data?.attributes);
  }

  /**
   * Permet de retourner les marketingConsent du client
   * @param kfId
   * @param vendorToken
   */
  async fetchMarketingConsentData(kfId: string, vendorToken: string): Promise<any> {
    const filters = new URLSearchParams();
    filters.append('include', 'marketingConsent');

    try {
      const response = await this.makeApiCall({
        method: HttpMethod.GET,
        url: `${this._colleagueBaseApi}/${kfId}/?${filters.toString()}`,
        responseType: 'json',
        token: vendorToken,
        withCredentials: false,
      });
      return {
        marketingConsent:
          response?.included !== undefined ? response?.included[0].attributes?.marketingChannels : undefined,
      };
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /**
   * Recherche des clients en fonction des critères donnés
   */
  async searchCustomers(searchInputs: SearchInput, vendorToken: string): Promise<PagedCustomerResponse> {
    const filters = new URLSearchParams();

    if (searchInputs.email) filters.append('filter[email]', this.normalizeValue(searchInputs.email));
    if (searchInputs.lastname) filters.append('filter[familyName]', this.normalizeValue(searchInputs.lastname));
    if (searchInputs.zipcode) filters.append('filter[postalCode]', this.normalizeValue(searchInputs.zipcode));
    if (searchInputs.phone) filters.append('filter[phoneNumber]', this.normalizeValue(searchInputs.phone));
    if (searchInputs.customerId) {
      filters.append('filter[customerNumber]', this.normalizeCustomerId(searchInputs.customerId));
    }
    if (searchInputs.fidelityId) {
      filters.append('filter[membershipNumber]', this.normalizeValue(searchInputs.fidelityId));
    }

    const size = '5';
    const page = Math.ceil((searchInputs.first + 1) / Number.parseInt(size));
    filters.append('page[number]', page.toString());

    filters.append('page[size]', size);

    filters.append('include', 'marketingConsent');

    try {
      const response = await this.makeApiCall({
        method: HttpMethod.GET,
        url: `${this._colleagueBaseApi}?${filters.toString()}`,
        responseType: 'json',
        token: vendorToken,
        withCredentials: false,
      });
      return {
        customers: response?.data?.map((c: any) => plainToInstance(Customer, c.attributes)) || [],
        meta: response?.meta || {
          totalSize: 0,
          pageNumber: 1,
          pageSize: size,
        },
        links: response?.links || {
          first: '',
          last: '',
          next: '',
          self: '',
        },
      };
    } catch (error) {
      logError(error);
      const errorCode = error?.error?.errors?.[0]?.code;
      if (errorCode === 'SEARCH_CRITERIA_TOO_WIDE') {
        throw {
          code: 999,
          description: 'Les résultats de votre recherche sont trop nombreux, merci de préciser',
        };
      } else if (errorCode === 'CUSTOMER_INVALID_EMAIL') {
        throw { code: 999, description: "L'email renseigné n'est pas valide" };
      }
      throw error;
    }
  }

  /**
   * Permet de vérifier si l'adresse est bonne : s'il y a la ville, le code postal, le pays, la rue et le numéro
   * @param address l'adresse à vérifier
   * @returns
   */
  private validateAddress(address: Address): boolean {
    return !(!address?.city || !address?.postalCode || !address?.country || !address.line1 || !address.num);
  }

  /**
   * Crée une adresse pour un client donné
   */
  private async createCustomerAddress(
    customerKfId: string,
    createCustomer: CreateCustomer,
    token: string
  ): Promise<any> {
    delete createCustomer.email;
    delete createCustomer.marketingChannels;
    delete createCustomer.dateOfBirth;

    try {
      const createCustomerInstance = plainToInstance(CreateCustomer, createCustomer);
      return await this.makeApiCall({
        method: HttpMethod.POST,
        url: `${this._colleagueBaseApi}/${customerKfId}/mainAddress`,
        token,
        body: {
          attributes: {
            contactDetails: {
              data: { attributes: instanceToPlain(createCustomerInstance) },
            },
          },
          type: 'contactInfo',
        },
        withCredentials: false,
      });
    } catch (err) {
      logError(err);
      throw new Error(ERR_ADDRESS_CREATION_FAILED);
    }
  }

  /**
   * Effectue un appel API avec les paramètres spécifiés
   */
  private async makeApiCall(params: HttpParams): Promise<any> {
    if (params.withCredentials !== false) params.withCredentials = true;

    return await firstValueFrom(
      this.restApi(params, {
        'Content-Type': 'application/vnd.api+json',
        Authorization: `Bearer ${params.token}`,
        'X-Tenant': TENANT,
      })
    );
  }

  private normalizeValue(val?: string): string {
    return val?.trim() ?? '';
  }

  private normalizeCustomerId(id?: string): string {
    if (!id) return '';
    return id.replace(/^SQ_/i, '').trim();
  }
}
